import React, { useState, useCallback, useEffect } from 'react';
import PricingPlans from './PricingPlans';
import { Link, useNavigate } from 'react-router-dom';
import { useSubscription } from '../../hooks/useSubscription';

interface TrialBannerProps {
  trialEndDate: Date | null;
}

const TrialBanner: React.FC<TrialBannerProps> = ({ trialEndDate }) => {
  const navigate = useNavigate();
  const [showPlans, setShowPlans] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { 
    refreshSubscription, 
    isCanceled, 
    isFree, 
    isTrial, 
    isExpired,
    status, 
    trialDaysRemaining 
  } = useSubscription();
  
  // Debug logging on mount
  useEffect(() => {
    console.log('TrialBanner - Subscription status:', {
      isCanceled,
      isFree,
      isTrial,
      isExpired,
      status,
      trialEndDate,
      trialDaysRemaining
    });
  }, [isCanceled, isFree, isTrial, isExpired, status, trialEndDate, trialDaysRemaining]);
  
  const handleUpgradeClick = () => {
    navigate('/pricing');
  };
  
  // Use a debounced refresh to prevent multiple calls
  const handleRefreshClick = useCallback(async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      await refreshSubscription();
      // Add a slight delay to show the spinner
      setTimeout(() => {
        setIsRefreshing(false);
      }, 1000);
    } catch (error) {
      setIsRefreshing(false);
    }
  }, [refreshSubscription, isRefreshing]);

  // Determine the banner text based on subscription status
  const getBannerTitle = () => {
    if (isCanceled) return "Subscription Canceled";
    if (isExpired) return "Trial Expired";
    if (isTrial) {
      return trialDaysRemaining > 0 ? "Trial License" : "Trial Expired";
    }
    if (isFree || status === 'free') return "Free Plan";
    return "Trial License"; // Default fallback
  };

  const getBannerDescription = () => {
    if (isCanceled) return "Your subscription has been canceled";
    if (isExpired) return "Your trial period has ended. Free plan is no longer available.";
    if (isTrial) {
      if (trialDaysRemaining > 0) {
        return `${trialDaysRemaining} ${trialDaysRemaining === 1 ? 'day' : 'days'} remaining`;
      } else if (status === 'trial' && trialDaysRemaining > 0) {
        // Double-check we're not in an expired state when we have trial status and days remaining
        return `${trialDaysRemaining} ${trialDaysRemaining === 1 ? 'day' : 'days'} remaining`;
      } else {
        return "Trial Ended";
      }
    }
    if (isFree || status === 'free') return "Limited features available";
    return "";
  };

  const getActionButtonText = () => {
    if (isCanceled) return "Resubscribe";
    if (isExpired) return "Subscribe Now";
    if (isTrial) {
      return trialDaysRemaining > 0 ? "Upgrade Now" : "Subscribe Now";
    }
    if (isFree || status === 'free') return "Upgrade to Premium";
    return "Upgrade Now";
  };
  
  return (
    <>
      <div className={`bg-gradient-to-r ${isExpired ? 'from-amber-500 to-amber-600' : 'from-[#0078d4] to-[#106ebe]'} text-white h-10 flex items-center shadow-md`}>
        <div className="container mx-auto flex justify-between items-center px-4">
          <div className="flex items-center space-x-2">
            <div className={`bg-white bg-opacity-20 rounded-full p-1 mr-1`}>
              {isExpired ? (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 flex-shrink-0">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 flex-shrink-0">
                  <path d="M11 2a1 1 0 10-2 0v2H7a1 1 0 000 2h2v2a1 1 0 102 0V6h2a1 1 0 100-2h-2V2z" />
                  <path fillRule="evenodd" d="M4 5a3 3 0 013-3h6a3 3 0 013 3v10a3 3 0 01-3 3H7a3 3 0 01-3-3V5zm3-1h6a1 1 0 011 1v10a1 1 0 01-1 1H7a1 1 0 01-1-1V5a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="flex flex-col xs:flex-row xs:items-center">
              <span className="text-sm font-semibold whitespace-nowrap">
                {getBannerTitle()}
              </span>
              <span className="text-xs font-normal xs:ml-2 opacity-90">
                {getBannerDescription()}
              </span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleRefreshClick}
              className="text-white hover:bg-white hover:bg-opacity-10 rounded-full p-1 focus:outline-none focus:ring-1 focus:ring-white"
              title="Refresh subscription status"
              disabled={isRefreshing}
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
           
            <button 
              onClick={handleUpgradeClick} 
              className={`${isExpired ? 'bg-white hover:bg-gray-100 text-amber-600' : 'bg-white hover:bg-gray-100 text-[#0078d4]'} font-medium text-xs px-3 py-1 rounded focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-1 focus:ring-offset-[#0078d4] transition-colors`}
            >
              {getActionButtonText()}
            </button>
          </div>
        </div>
      </div>
      
      {/* Pricing Plans Modal */}
      
    </>
  );
};

export default TrialBanner; 